import { NgFor, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { CrmPromoIdsMap, EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { RxFor } from '@rx-angular/template/for';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, shareReplay } from 'rxjs';

import { SourceOfPick } from '../betslip-base/models/pick-models';
import { BadgeComponent } from '../crm-promotion/badge.component';
import { MarketHelpComponent } from './market-help/market-help.component';
import { OptionGroup, OptionGroupService } from './option-group.service';
import { OptionPanelBottomActionComponent, OptionPanelBottomActionState, ShowMoreData } from './option-panel-bottom-action.component';
import { OptionComponent } from './option.component';
import { SHEET_VIEW_OPEN } from './sheet.token';

@Component({
    selector: 'ms-over-under-option-group',
    templateUrl: './over-under-option-group.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './over-under-option-group.scss',
    standalone: true,
    imports: [RxFor, NgFor, NgTemplateOutlet, MarketHelpComponent, BadgeComponent, OptionComponent, OptionPanelBottomActionComponent],
})
export class OverUnderOptionGroupComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() optionGroup: EventOptionGroup;
    @Input() showMarketHelp: boolean;
    @Input() crmPromoIdsMap: CrmPromoIdsMap;
    @Input() eventHelpText?: string;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';
    @Input() optionsLimitOverride?: number;
    @Output() showAllRequested = new EventEmitter<void>();

    readonly isInSheetView = Boolean(inject(SHEET_VIEW_OPEN, { optional: true }));
    private optionGroups$ = new BehaviorSubject<OptionGroup[]>([]);
    private visibleOptionsLimit$ = new BehaviorSubject<number | undefined>(undefined);
    showMoreData: ShowMoreData;
    displayLineValueInSquares = this.marketGroupingConfig?.shouldShowLineValueInSquares ?? false;

    visibleOptionGroups$ = combineLatest([this.optionGroups$, this.visibleOptionsLimit$]).pipe(
        map(([optionGroups, visibleOptionsLimit]) => {
            if (visibleOptionsLimit != null) {
                return optionGroups.filter((g, i) => i < visibleOptionsLimit);
            }

            return optionGroups;
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
    );

    visibleOptionGroups = toSignal(
        this.visibleOptionGroups$.pipe(
            map((o) => o.length),
            distinctUntilChanged(),
        ),
        { initialValue: 0 },
    );

    expanded = false;
    showMoreLessButtonVisible = false;

    constructor(
        public sitecore: Sitecore,
        private optionGroupService: OptionGroupService,
        private marketGroupingConfig: MarketGroupingConfig,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.optionGroup) {
            this.optionGroups$.next(this.optionGroupService.getOptionGroups(this.optionGroup, (option) => option.marketAttribute));
        }
        if (changes.optionsLimitOverride) {
            this.calculateMaxNumberOfVisibleOptions();
        }
        if (changes.optionsLimitOverride || changes.optionGroup) {
            this.showMoreLessButtonVisible =
                this.optionGroups$.value.length > (this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines);
            const totalOptions = this.optionGroups$.value.length;
            this.showMoreData = {
                totalCount: totalOptions,
                initialVisibleCount: this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines,
                pageSize: totalOptions,
            };
        }
    }

    trackOption(index: number, option: EventOption): number {
        return option.id;
    }

    trackGroup(index: number, group: OptionGroup): string {
        return group.attribute || '';
    }

    isMarketPromoted(optionGroupId: number, templateId: number): boolean {
        return !!this.crmPromoIdsMap.markets[optionGroupId] || (templateId != null && !!this.crmPromoIdsMap.templateIds[templateId]);
    }

    onShowMore({ state }: { state: OptionPanelBottomActionState; visibleCount: number }): void {
        this.expanded = state !== 'collapsed';
        this.calculateMaxNumberOfVisibleOptions();
    }

    onShowMoreLessButtonToggle(): void {
        this.expanded = !this.expanded;
        this.visibleOptionsLimit$.next(
            this.expanded ? undefined : (this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines),
        );
    }

    private calculateMaxNumberOfVisibleOptions(): void {
        this.visibleOptionsLimit$.next(
            this.isInSheetView || this.expanded ? undefined : (this.optionsLimitOverride ?? this.marketGroupingConfig.overUnderGroupMaxNumberOfLines),
        );
    }
}
